'use client';
import {
  Dialog,
  Flex,
  Input,
  InputChips,
  ButtonGroup,
  Button,
  ResultListItem,
  Alert,
  Icon,
  Text,
} from '@/_components';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { EditTagsDialogProps } from './EditTagsDialog.types';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import {
  addDocumentTag,
  updateDocumentTag,
  deleteDocumentTag,
} from '@/_api/client-documents';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { DocumentsContext } from '@/_contexts/DocumentsProvider';
import styles from './EditTagsDialog.module.css';

export const EditTagsDialog: FC<EditTagsDialogProps> = ({ item, onClose }) => {
  const { firm, reloadFirm } = useContext(FirmSettingsContext);
  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);
  const { reloadFiles, companyId } = useContext(DocumentsContext);

  const [chips, setChips] = useState<Set<string>>(new Set());
  const [chipText, setChipText] = useState<string>('');

  const [newTagText, setNewTagText] = useState<string>('');
  const [isEditingFirmTags, setIsEditingFirmTags] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setIsEditingFirmTags(false);
    if (item) {
      setChips(new Set(item.tags));
    }
  }, [item]);

  const isDuplicate: boolean = !!firm?.document_tags.includes(newTagText);

  const allDocumentTags: Array<ResultListItem> = useMemo(() => {
    const selectedTags =
      item?.tags?.map((tag) => ({
        name: tag,
        value: tag,
      })) || [];

    const firmTags =
      firm?.document_tags?.map((tag) => ({
        name: tag,
        value: tag,
      })) || [];

    const uniqueTags = Array.from(
      new Map(
        [...selectedTags, ...firmTags].map((tag) => [tag.value, tag]),
      ).values(),
    );

    return uniqueTags;
  }, [item, firm?.document_tags]);

  const addNewTag = async () => {
    try {
      await addDocumentTag({
        tag: newTagText,
        token,
      });

      await reloadFirm();
    } catch (error) {
      addToast('error', 'Issue adding new tag, please try again later.', {
        error,
      });
    } finally {
      setNewTagText('');
    }
  };

  const deleteTag = async (tag: string) => {
    try {
      await deleteDocumentTag({
        tag,
        token,
      });

      await reloadFirm();
    } catch (error) {
      addToast('error', 'Issue adding new tag, please try again later.', {
        error,
      });
    } finally {
      setNewTagText('');
    }
  };

  const onChangeTagText = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewTagText(event.target.value);
  };

  const onChipText = (event: React.ChangeEvent<HTMLInputElement>) =>
    setChipText(event.target.value);

  const updateSelectedTags = async () => {
    setIsLoading(true);
    const tags = Array.from(chips.values());
    try {
      await updateDocumentTag({
        tags,
        extractedDocumentId: item?.extracted_document_id!,
        companyId: companyId!,
        token,
      });
      await reloadFiles();
    } catch (error) {
      addToast('error', 'Issue saving selected tags, please try again later.', {
        error,
      });
    } finally {
      onClose();
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      isOpen={!!item}
      title="Document Tags"
      onClose={onClose}
      isOverflowing={true}
      headerButtons={
        <Button
          intent="ghost"
          onClick={() => {
            setIsEditingFirmTags(!isEditingFirmTags);
          }}
          size="small"
          isIconOnly={true}
          iconLeft={isEditingFirmTags ? 'arrow-left' : 'tag'}
          value={isEditingFirmTags ? 'Back' : 'Add New Tags'}
          tooltip={isEditingFirmTags ? 'Back' : 'Add New Tags'}
          withTooltip={true}
        />
      }
    >
      <Dialog.Content>
        <Flex isColumn={true} gap={24}>
          {isEditingFirmTags ? (
            <Flex gap={12} isColumn={true}>
              {!firm?.document_tags && (
                <Alert type="info" icon="info">
                  No Document tags has been created yet.
                </Alert>
              )}
              <Flex isWrapping={true} gap={2}>
                {firm?.document_tags?.map((tag, key) => {
                  return (
                    <Flex
                      key={key}
                      isBetween={true}
                      gap={4}
                      className={styles.documentTag}
                    >
                      <Flex isYCentered={true} gap={4}>
                        <Icon name="tag" size={16} />
                        <Text type="small" fontWeight="medium">
                          {tag}
                        </Text>
                      </Flex>
                      <ButtonGroup>
                        <Button
                          intent="danger-secondary"
                          iconLeft="trash"
                          value="Remove Tag"
                          isIconOnly={true}
                          size="tiny"
                          onClick={() => deleteTag(tag)}
                        />
                      </ButtonGroup>
                    </Flex>
                  );
                })}
              </Flex>
              <Flex gap={6} yAlign="end">
                <Input
                  label="New document tag"
                  autoFocus={true}
                  value={newTagText}
                  onChange={onChangeTagText}
                />
                <ButtonGroup align="right">
                  <Button
                    intent="primary"
                    onClick={addNewTag}
                    value="Add Tag"
                    iconLeft="plus"
                    isDisabled={!newTagText.length || isDuplicate}
                    withTooltip={isDuplicate}
                    tooltip="Tag already exist"
                  />
                </ButtonGroup>
              </Flex>
            </Flex>
          ) : (
            <Flex isColumn={true} gap={8}>
              <InputChips
                label="Selected Tags"
                chips={chips}
                onChange={onChipText}
                results={allDocumentTags}
                setChips={setChips}
                value={chipText}
                placeholder="Type or select from options"
                clearable
              />
              <ButtonGroup align="right">
                <Button
                  intent="primary"
                  value={isLoading ? 'Saving...' : 'Save'}
                  onClick={updateSelectedTags}
                  isLoading={isLoading}
                />
              </ButtonGroup>
            </Flex>
          )}
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
